<template>
  <div>
    <v-dialog
      :model-value="showDialog"
      width="auto"
      @update:model-value="handleClose"
    >
      <v-card class="card br-5">
        <v-toolbar
          color="rgba(0, 0, 0, 0)"
          class="mb-3"
        >
          <div class="pl-4 card__instructions-text">
            {{ exportMessage }}
          </div>
          <template #append>
            <v-btn
              icon="mdi-close"
              variant="text"
              @click="handleClose()"
            />
          </template>
        </v-toolbar>
        <v-card-text class="pt-0 pb-2 px-4">
          <v-container
            class="pa-0"
            fluid
            style="max-height: 100vh"
          >
            <v-row class="d-flex flex-column flex-sm-row">
              <v-col
                sm="4"
                cols="12"
                class="d-flex align-center py-0 pr-0"
              >
                <v-select
                  v-if="options.length > 1"
                  ref="optionSelect"
                  v-model="selectedOption"
                  :items="options"
                  item-title="dropdownLabel"
                  density="compact"
                  return-object
                  hide-details
                >
                  <template #item="{ props, item }">
                    <v-list-item
                      v-bind="props"
                      density="compact"
                      :title="item.raw.dropdownLabel"
                    />
                  </template>
                </v-select>
                <div v-else>
                  <span class="text-body-1">
                    {{ selectedOption.dropdownLabel }}
                  </span>
                </div>
              </v-col>
              <v-col
                v-if="selectedOption.value === 'add'"
                data-testid="addToMapColumn"
                class="py-0 dropdown-column"
              >
                <input
                  v-if="!selectedMap?.id"
                  id="chooseMapSearchbox"
                  v-model="searchTerm"
                  type="text"
                  class="card__map-search outlined-input"
                  placeholder="Search map name..."
                  @keydown="handleKeys"
                >
                <div
                  v-else
                  class="card__map-search card__selected"
                >
                  <div class="">
                    {{ selectedMap.name }}
                  </div>
                  <XMarkIcon
                    class="card__selected__xmark-icon"
                    :weight="0.2"
                    @click="handleResultClicked(null)"
                  />
                </div>
                <div
                  v-if="searchTerm && (searchTerm !== selectedMap?.name)"
                  ref="dropdownContent"
                  class="dropdown-content"
                >
                  <SearchResult
                    v-for="(result, index) in algoliaMaps"
                    :id="`result-${index}`"
                    :key="result.id"
                    ref="searchResult"
                    :result="result"
                    :style="computeStyle(index)"
                    @handleClick="handleResultClicked"
                  />
                  <LoadIcon
                    v-if="searchLoading && algoliaMaps.length === 0"
                    class="d-flex justify-center"
                  />
                  <div
                    v-else-if="!searchLoading && algoliaMaps.length === 0"
                    class="dropdown-content__no-results"
                  >
                    No results found
                  </div>
                </div>
              </v-col>
              <v-col
                v-else-if="selectedOption.value === 'create'"
                data-testid="createMapColumn"
                class="py-0 dropdown-column"
              >
                <input
                  v-model="newMapName"
                  type="text"
                  class="card__map-search outlined-input"
                >
              </v-col>
            </v-row>
          </v-container>
          <ExportMapSettings
            :export-field-settings="exportFieldSettings"
            :export-items="exportItems"
            @setSettings="setExportSettings"
            @discovering="discoveringFields = $event"
          />
          <CreateSharedMapButton
            v-if="selectedOption.value === 'add'"
            class="mt-4"
            data-testid="addToMapButton"
            loading-text="Adding to Shared Map..."
            btn-text="Add to Shared Map"
            :export-items="exportItems"
            :disabled="!selectedMap?.id || !exportItems || discoveringFields"
            :selected-map="selectedMap"
            :export-field-settings="exportFieldSettings"
            @close="handleClose"
          />
          <CreateSharedMapButton
            v-else-if="selectedOption.value === 'create'"
            class="mt-4"
            data-testid="createMapButton"
            loading-text="Creating Shared Map..."
            btn-text="Create Shared Map"
            :disabled="!newMapName || !newMapName.length || !exportItems || discoveringFields"
            :export-items="exportItems"
            :new-map-name="newMapName"
            :export-field-settings="exportFieldSettings"
            @close="handleClose"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
// Services & Helpers
import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {connectHits, connectSearchBox} from 'instantsearch.js/es/connectors';
import {currentUser} from '@/services/FirestoreService';
import {PropType} from 'vue';
import {getSharedMapsUsers} from '@/services/BackendService';
import type {SharedMapsUser} from '@/services/BackendService';
import type {SearchBoxRenderState} from 'instantsearch.js/es/connectors/search-box/connectSearchBox';
import {gatherExportItems} from '@/lib/classes/SharedMap';
import type {SharedMap} from '@/lib/classes/SharedMap';
import type {ExportFieldSettings} from '@/lib/classes/SharedMap/exportFields';
import type {BaseHit} from 'instantsearch.js';
import type {AcceptedOperation, AlgoliaConfig} from './UpsertSharedMap';
import type {VSelect} from 'vuetify/components';

// Components
import SearchResult from '@/components/SearchBar/sections/SearchResult.vue';
import LoadIcon from '@/components/svgs/LoadIcon.vue';
import CreateSharedMapButton from '../CreateSharedMapButton.vue';
import ExportMapSettings from './ExportMapSettings.vue';
import XMarkIcon from '@/components/svgs/XMarkIcon.vue';
import {mapActions, mapState} from 'vuex';

interface MapExportOption {
  dropdownLabel: string;
  buttonText: string;
  buttonLoadingText: string;
  value: AcceptedOperation;
}

export default {
  name: 'UpsertSharedMapDialog',
  components: {
    SearchResult,
    LoadIcon,
    CreateSharedMapButton,
    ExportMapSettings,
    XMarkIcon,
  },
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    maxItems: {
      type: Number,
      required: true,
    },
    exportMessage: {
      type: String,
      required: true,
    },
    operations: {
      type: Array as PropType<AcceptedOperation[]>,
      required: true,
    },
    collections: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },
  emits: ['input', 'close'],
  data() {
    const selectedOptions: MapExportOption[] = [
      {
        dropdownLabel: 'Create New Map',
        buttonText: 'Create Shared Map',
        buttonLoadingText: 'Creating Shared Map...',
        value: 'create',
      },
      {
        dropdownLabel: 'Add to Existing Map',
        buttonText: 'Add to Shared Map',
        buttonLoadingText: 'Adding to Shared Map...',
        value: 'add',
      },
    ];
    return {
      selectedMap: null as {id: string; name: string} | null,
      searchTerm: '',
      algoliaResults: [] as BaseHit[],
      searchLoading: true,
      newMapName: 'Untitled Map',
      focused: -1,
      smapsUsers: [] as SharedMapsUser[],
      refineSearchCallback: null as SearchBoxRenderState['refine'] | null,
      selectedOption: selectedOptions.find((o) => o.value === this.operations[0]) as MapExportOption,
      options: selectedOptions.filter((o) => this.operations.includes(o.value as AcceptedOperation)),
      exportFieldSettings: new Map() as ExportFieldSettings,
      /** Hydrated items per collection, gathered once per dialog open; null while gathering. */
      exportItems: null as Map<string, unknown[]> | null,
      gatherCount: 0,
      /** True from when items are gathered until ExportMapSettings signals field discovery is done. */
      discoveringFields: false,
    };
  },
  computed: {
    ...mapState({
      sharedMap: (state: any) => state.sharedMap as SharedMap,
      algoliaConfig: (state: any) => state.algoliaConfig as AlgoliaConfig,
    }),
    algoliaMaps() {
      return this.algoliaResults;
    },
  },
  watch: {
    searchTerm(newValue) {
      this.searchLoading = true;
      if (!newValue) {
        this.focused = -1;
      }
      this.updateSearches();
    },
    selectedOption() {
      // reset variables for each option
      this.selectedMap = null;
      this.searchTerm = '';
      this.newMapName = 'Untitled Map';
      // blur focus on the vuetify select
      (this.$refs.optionSelect as VSelect).blur();
    },
    showDialog: {
      immediate: true,
      handler(newValue: boolean) {
        if (newValue) {
          void this.gatherItems();
        } else {
          this.exportFieldSettings = new Map();
          this.exportItems = null;
          this.discoveringFields = false;
        }
      },
    },
  },
  async mounted() {
    await this.loadAlgoliaConfig();
    await Promise.all([
      this.initializeAlgolia(),
      this.fetchUsers(),
    ]);
  },
  methods: {
    ...mapActions(['loadAlgoliaConfig', 'addMessage']),
    handleClose() {
      this.newMapName = 'Untitled Map';
      this.searchLoading = false;
      this.searchTerm = '';
      this.selectedMap = null;
      this.$emit('close');
    },
    updateSearches() {
      if (!this.refineSearchCallback) return;
      this.refineSearchCallback(this.searchTerm);
    },
    handleResultClicked(result: any) {
      if (!result) {
        this.selectedMap = null;
        this.searchTerm = '';
        this.focused = -1;
        return;
      }
      this.selectedMap = {
        id: result.id,
        name: `${result.name} (${result.createdOn.name})`,
      };
      this.searchTerm = this.selectedMap.name;
    },
    updateResults({hits}: {hits: BaseHit[]}, isFirstRender: boolean) {
      if (!isFirstRender && this.searchTerm) {
        this.algoliaResults = hits
          .map((hit) => {
            if (!this.allowedToAccess(hit)) return null;
            const result = {
              ...hit,
              customOptions: {
                iconType: 'none',
                label: [`${hit.name} (${hit.createdOn.name})`],
                style: 'height: 15px; padding: 15px 0;',
                clickHandler: this.handleResultClicked,
              },
            };
            result.customOptions.clickHandler = () => this.handleResultClicked(result);
            return result;
          }).filter((hit) => hit !== null);
        this.searchLoading = false;
      }
    },
    allowedToAccess(hit: any) {
      // Don't show the current shared map in the search results, if applicable
      if (this.sharedMap && hit.name === this.sharedMap.name) return false;

      const shareSettings = hit.share;
      if (!shareSettings) return false;

      // If the user created the map, they have access
      // Checks name bc we don't have access to the user's SMAPS uid
      if (hit.createdOn?.name === currentUser()!.displayName) {
        return true;
      }

      // If the user is in the list of users, they have access
      const user = this.smapsUsers.find((user) => user.email === currentUser()!.email);
      const userUid = user?.uid;
      if (shareSettings.users.includes(userUid)) {
        return true;
      }

      // If the map is editable, the user has access
      return shareSettings.accessLevel === 'edit';
    },
    computeStyle(index: number) {
      return (index === this.focused) ? 'background-color: #EAE8E8;' : '';
    },
    /** Fetches and stores information about users in SMAPS Firebase project. */
    async fetchUsers() {
      try {
        this.smapsUsers = await getSharedMapsUsers({forceTokenRefresh: true});
      } catch (error) {
        if (error instanceof Error) {
          error.message = 'Error fetching users: ' + error.message;
        }
        throw error;
      }
    },
    handleKeys(event: KeyboardEvent) {
      const code = event.code;
      // Empty return if no results returned
      if (!this.algoliaResults.length) return;
      if (code === 'ArrowUp') {
        // Keying through results list logic
        if (this.focused <= 0) {
          this.focused = this.algoliaResults.length - 1;
        } else {
          this.focused--;
        }
      }
      if (code === 'ArrowDown') {
        // Keying through results list logic
        if (this.focused === this.algoliaResults.length - 1) {
          this.focused = 0;
        } else {
          this.focused++;
        }
      }

      const dropdownContent = this.$refs.dropdownContent as HTMLDivElement;
      const searchResultRef = dropdownContent?.children[this.focused] as HTMLDivElement;
      if (!searchResultRef) return;

      const dropdownHeight = dropdownContent.clientHeight;
      let scrollTop = dropdownContent.scrollTop;

      // If the focused search result is above or below the visible area of the dropdown, scroll to bring it into view
      if (searchResultRef.offsetTop < scrollTop ||
        searchResultRef.offsetTop + searchResultRef.offsetHeight > scrollTop + dropdownHeight) {
        scrollTop = searchResultRef.offsetTop;
      }

      // Set the scrollTop property of the dropdown content to the new scrollTop value
      dropdownContent.scrollTop = scrollTop;

      // If the inside div needs to be scrolled to show the focused SearchResult on keypress, do so
      if (dropdownContent.clientHeight < searchResultRef.offsetTop || searchResultRef.offsetTop < 10) {
        searchResultRef.scrollIntoView();
      }

      if (code === 'Enter' && this.focused >= 0) {
        // Remove focus from search bar input
        (document.activeElement as HTMLElement).blur();
        // Trigger the appropriate search result's click handler
        searchResultRef.dispatchEvent(new MouseEvent('click'));
        // Reset the index
        this.focused = -1;
      }
    },
    initializeAlgolia() {
      const search = instantsearch({
        searchClient: algoliasearch(this.algoliaConfig.appId, this.algoliaConfig.apiKey),
        indexName: this.algoliaConfig.indexName,
        future: {
          preserveSharedStateOnUnmount: true,
        },
      });

      const renderSearchBox = (renderOptions: SearchBoxRenderState, isFirstRender: boolean) => {
        if (isFirstRender) {
          this.refineSearchCallback = renderOptions.refine;
        }
      };

      const customSearchBox = connectSearchBox(renderSearchBox);
      const customHits = connectHits(this.updateResults);

      search.addWidgets([
        customSearchBox({
          // @ts-expect-error Apparently 'container' is not a valid property, yet it works. Dunno.
          container: document.querySelector('#chooseMapSearchbox'),
        }),
        customHits({
          // @ts-expect-error
          container: document.querySelector('#hits'),
        }),
      ]);
      search.start();
    },
    setExportSettings(settings: ExportFieldSettings) {
      this.exportFieldSettings = settings;
    },
    /** Gathers (and, under backend filtering, hydrates) the items this dialog can export. */
    async gatherItems() {
      const gatherCount = ++this.gatherCount;
      let items: Map<string, unknown[]>;
      // `failures` is [] on a clean gather and lists the collections that failed to hydrate; it is
      // only null when the whole gather threw (the catch below), so `=== null` never matches success.
      let failures: string[] | null;
      try {
        ({items, failures} = await gatherExportItems(this.collections));
      } catch (error) {
        console.error('Failed to gather export items:', error);
        items = new Map();
        failures = null;
      }
      // Ignore a stale gather from a dialog that was closed or reopened mid-flight.
      if (gatherCount !== this.gatherCount || !this.showDialog) return;
      this.exportItems = items;
      // Discovery runs in ExportMapSettings next; keep the buttons disabled until it signals it is done.
      this.discoveringFields = true;
      // Surface a failure rather than a misleading "No fields were detected".
      if (failures === null) {
        this.addMessage({
          header: 'Export data could not be loaded',
          messageType: 'danger',
          body: 'Something went wrong loading this export. Please try again.',
        });
      } else if (failures.length) {
        this.addMessage({
          header: 'Some map data could not be loaded',
          messageType: 'danger',
          body: 'These layers were left out of the export:',
          bulletedList: failures,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  padding: 10px 10px 20px 10px;
  width: 615px;
  @include main.custom-width(640, auto);

  &__instructions-text {
    color: main.$npGray;
    font-weight: 800;
    @media(max-width: 700px) {
      max-width: 400px;
    }
  }

  &__map-search {
    height: 40px;
    width: 100%;
  }
  &__selected {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-left: 11px;
    border-style: solid;
    border-width: 1px;
    border-radius: 4px;
    border-color: #dbdbdb;
    color: #363636;

    &__xmark-icon {
      margin-left: auto;
      margin-right: 10px;
      height: 13px;
      width: 13px;
      cursor: pointer;
    }
  }
  .dropdown-column {
    @media (max-width: 600px) {
      padding-right: 0;
    }
  }
  .dropdown-content {
    border-radius: 2px;
    margin-top: 1px;
    padding: 0;
    position: absolute;
    overflow-x: hidden;
    max-height: 200px;
    width: 58%;
    z-index: 99;

    &__no-results {
      font-weight: bold;
      padding: 15px 5px;
      height: 15px;
      width: 100%;
      display: flex;
      align-items: center;
      cursor: default;
      font-size: 12px;
      max-height: 44px;
      margin-left: 4px;
    }
  }
}
:deep(.v-select .v-field.v-field) {
  padding-right: 5px;
}
:deep(.v-select__selection-text) {
  font-size: 14px;
}
:deep(.v-list-item-title) {
  font-size: 14px;
}
</style>
